import { useState, type FormEvent } from 'react';

import { Alert, Button, FormField, Input, RadioCardGroup } from '@playerbase/ui';
import {
  StudioSetupFormSchema,
  StudioRpcResponseSchema,
  TeamSizeSchema,
  UseCaseSchema,
  TEAM_SIZE_LABELS,
  USE_CASE_LABELS,
  type StudioRpcResponse,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// Option arrays are derived from the schema enum order so the UI stays in
// lockstep with the canonical ordering.
const TEAM_SIZE_OPTIONS = TeamSizeSchema.options.map((value) => ({
  value,
  label: TEAM_SIZE_LABELS[value],
}));

const USE_CASE_OPTIONS = UseCaseSchema.options.map((value) => ({
  value,
  label: USE_CASE_LABELS[value],
}));

// Translate raw RPC error tokens into human messages. Shared between the
// /welcome flow and the in-app create flow so both surfaces speak the same
// language when validation or auth fails.
export function studioRpcErrorMessage(raw: string): string {
  switch (raw) {
    case 'name_required':
      return 'Studio name is required.';
    case 'name_too_long':
      return 'Studio name must be 80 characters or fewer.';
    case 'invalid_team_size':
      return 'Please select a valid team size.';
    case 'invalid_use_case':
      return 'Please select a valid use case.';
    case 'studio_slug_collision':
      return 'Could not generate a unique identifier for your studio. Please try a slightly different name.';
    case 'not_authenticated':
      return 'Your session has expired. Please sign in again.';
    case 'not_a_member':
    case 'not_authorised':
      return 'You do not have permission to change this studio.';
    case 'studio_not_found':
      return 'This studio no longer exists.';
    default:
      return raw;
  }
}

type StudioSetupFormProps = {
  initialName: string;
  submitLabel: string;
  submittingLabel: string;
  onCreated: (studio: StudioRpcResponse) => void | Promise<void>;
};

export function StudioSetupForm({
  initialName,
  submitLabel,
  submittingLabel,
  onCreated,
}: StudioSetupFormProps): JSX.Element {
  const [name, setName] = useState(initialName);
  const [teamSize, setTeamSize] = useState('');
  const [useCase, setUseCase] = useState('');
  const [rpcError, setRpcError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isValid = StudioSetupFormSchema.safeParse({ name, teamSize, useCase }).success;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setRpcError('');

    const parsed = StudioSetupFormSchema.safeParse({ name, teamSize, useCase });
    if (!parsed.success) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('create_studio_with_owner', {
        p_name: parsed.data.name.trim(),
        p_team_size: parsed.data.teamSize,
        p_use_case: parsed.data.useCase,
      });

      if (error) {
        setRpcError(studioRpcErrorMessage(error.message));
        return;
      }

      const response = StudioRpcResponseSchema.safeParse(data);
      if (!response.success) {
        setRpcError('Studio created but the response was malformed. Please refresh the page.');
        return;
      }

      await onCreated(response.data);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-6" noValidate>
      {rpcError !== '' && (
        <Alert tone="danger">{rpcError}</Alert>
      )}

      <FormField>
        <FormField.Label>Studio name</FormField.Label>
        <FormField.Control>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="w-full"
          />
        </FormField.Control>
        <FormField.HelpText>
          This is how your studio will appear in PlayerBase. You can change it later.
        </FormField.HelpText>
      </FormField>

      <FormField>
        <FormField.Label>Team size</FormField.Label>
        <FormField.Control>
          <RadioCardGroup
            name="team-size"
            value={teamSize}
            onChange={setTeamSize}
            options={TEAM_SIZE_OPTIONS}
            orientation="grid"
          />
        </FormField.Control>
      </FormField>

      <FormField>
        <FormField.Label>How will you use PlayerBase?</FormField.Label>
        <FormField.Control>
          <RadioCardGroup
            name="use-case"
            value={useCase}
            onChange={setUseCase}
            options={USE_CASE_OPTIONS}
            orientation="vertical"
          />
        </FormField.Control>
      </FormField>

      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={!isValid || submitting}
      >
        {submitting ? submittingLabel : submitLabel}
      </Button>
    </form>
  );
}
